import { useMemo } from 'react';
import { di } from 'react-magnetic-di';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import { fireOperationalAnalytics } from '@atlassian/analytics-bridge/fire-analytics/fire-analytics';
import { usePfErrorUtils } from '@atlassian/proforma-common-core/error-utils-context';
import type {
	AsyncFieldConfiguration,
	AsyncFields,
} from '@atlassian/proforma-form-renderer/async-fields';
import type { ProformaForm } from '@atlassian/proforma-form-renderer/proforma-form';

import type { JiraFieldData } from '../../../../controllers/request-create-context-provider/types';

import { useAffectedServices } from './affected-services';
import { useCmdbObjects } from './cmdb-objects';
import { useLabels } from './labels';
import { useUsers } from './users';
import { useUsersConfigured } from './users-configured';
import { getSchemaKey } from './utils/get-schema-key';
import { isUserField } from './utils/is-user-field';
import { resolveSchemaConfig } from './utils/resolve-schema-config';
import { resolveUserConfig } from './utils/resolve-user-config';
import { withLoadOptionsAnalytics } from './utils/with-load-options-analytics';

export { withLoadOptionsAnalytics } from './utils/with-load-options-analytics';
export {
	AFFECTED_SERVICES_SCHEMA_CUSTOM,
	CMDB_SCHEMA_CUSTOM,
	LABELS_SCHEMA_CUSTOM,
	LABELS_SCHEMA_SYSTEM,
} from './constants';

export const buildAsyncConfigs = (
	proformaForm: ProformaForm,
	jiraFieldData: JiraFieldData,
	affectedServicesFn: ReturnType<typeof useAffectedServices>,
	cmdbObjectsFn: ReturnType<typeof useCmdbObjects>,
	labelsFn: ReturnType<typeof useLabels>,
	usersFn: ReturnType<typeof useUsers>,
	usersConfiguredFn: ReturnType<typeof useUsersConfigured>,
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	errorUtils: ReturnType<typeof usePfErrorUtils>,
): AsyncFields => {
	const asyncConfigs: AsyncFields = {};
	const questions = Object.entries(proformaForm.design.questions);

	questions.forEach(([fieldId, field]) => {
		const jiraFieldKey = field.jiraField;
		let config: AsyncFieldConfiguration | undefined;

		if (isUserField(field.type)) {
			config = resolveUserConfig(jiraFieldKey, jiraFieldData, usersConfiguredFn, usersFn);
		} else if (jiraFieldKey) {
			const fieldData = jiraFieldData.get(jiraFieldKey);
			if (fieldData !== undefined) {
				config = resolveSchemaConfig(
					jiraFieldKey,
					fieldData,
					affectedServicesFn,
					cmdbObjectsFn,
					labelsFn,
					proformaForm,
				);
			}
		}

		if (config) {
			asyncConfigs[fieldId] = withLoadOptionsAnalytics(
				config,
				getSchemaKey(jiraFieldKey, jiraFieldData),
				createAnalyticsEvent,
				errorUtils,
			);
		}
	});

	return asyncConfigs;
};

export const useAsyncConfig = (
	jiraFieldData: JiraFieldData,
	proformaForm: ProformaForm,
): AsyncFields => {
	di(
		useAffectedServices,
		useCmdbObjects,
		useLabels,
		useUsers,
		useUsersConfigured,
		usePfErrorUtils,
		fireOperationalAnalytics,
	);

	const affectedServicesFn = useAffectedServices();
	const cmdbObjectsFn = useCmdbObjects();
	const labelsFn = useLabels();
	const usersFn = useUsers();
	const usersConfiguredFn = useUsersConfigured();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const errorUtils = usePfErrorUtils();

	return useMemo(
		() =>
			buildAsyncConfigs(
				proformaForm,
				jiraFieldData,
				affectedServicesFn,
				cmdbObjectsFn,
				labelsFn,
				usersFn,
				usersConfiguredFn,
				createAnalyticsEvent,
				errorUtils,
			),
		[
			affectedServicesFn,
			cmdbObjectsFn,
			createAnalyticsEvent,
			errorUtils,
			jiraFieldData,
			labelsFn,
			proformaForm,
			usersConfiguredFn,
			usersFn,
		],
	);
};
